import * as AK from '@ariakit/react'
import {Icon} from 'quickstart/components/content/Icon'
import {Image} from 'quickstart/components/content/Image'
import {Link} from 'quickstart/components/content/Link'
import {IconButton} from 'quickstart/components/controls/IconButton'
import {Container} from 'quickstart/components/layout/Container'
import {NavLink, type NavLinkProps} from 'quickstart/components/layout/NavLink'
import {Stack} from 'quickstart/components/layout/Stack'
import {ProfileMenu} from 'quickstart/components/tizra/ProfileMenu'
import {QuickSearch} from 'quickstart/components/tizra/QuickSearch'
import {useApi, useIsomorphicLayoutEffect} from 'quickstart/hooks'
import {dataProps} from 'quickstart/theme'
import {tid} from 'quickstart/utils'
import {type ComponentProps, useState} from 'react'
import {Divider} from '../Divider'
import {type UseProfileItemsProps, useNavItems, useProfileItems} from './hooks'
import S from './styles.module.scss'

type MenuItemProps = ComponentProps<typeof NavLink>

const MenuItem = (props: MenuItemProps) => (
  <div className={S['menu-item']}>
    <NavLink {...props} />
  </div>
)

interface NavButtonProps
  extends Omit<ComponentProps<typeof IconButton>, 'left' | 'right'> {
  left?: boolean
  right?: boolean
}

const NavButton = ({left, right, ...props}: NavButtonProps) => {
  return (
    <IconButton
      variant="stealth"
      className={S['nav-button']}
      {...dataProps({
        hug: left ? 'left' : right ? 'right' : null,
      })}
      {...props}
    />
  )
}

export interface NavProps {
  bookshelfConfig: UseProfileItemsProps['bookshelfConfig']
  logoLink: string
  logoUrl: string
  quickSearchPlaceholder: string
  includeCart: boolean
  includeSignIn: boolean
  nestedNavConfig: {items: NavLinkProps[]; profileItems: NavLinkProps[]}
  // protect agsint extra stuff from block config
  freemarker?: never
  nestedNavConfigJson?: never
  // old stuff from before nestedNavConfig
  links?: never
  profileMenu?: never
}

export const Nav = ({
  bookshelfConfig,
  logoLink,
  logoUrl,
  quickSearchPlaceholder,
  includeCart,
  includeSignIn,
  nestedNavConfig,
}: NavProps) => {
  const [open, setOpen] = useState(false)

  const {profileName, profileItems} = useProfileItems({
    ...nestedNavConfig,
    bookshelfConfig,
    includeSignIn,
  })

  const {items: navItems} = useNavItems(nestedNavConfig)

  const cartSize = useApi.cart().data?.length
  const notifyCart = !!cartSize

  useIsomorphicLayoutEffect(() => {
    if (open) {
      const saved = document.body.style.overflow
      window.scrollTo(0, 0)
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = saved
      }
    }
  }, [open])

  return (
    <AK.DisclosureProvider setOpen={setOpen}>
      <div>
        <Container className={S['logo-row']}>
          <div className={S['logo']}>
            <Link href={logoLink} variant="unstyled">
              <Image loading="eager" src={logoUrl} />
            </Link>
          </div>

          <div className={S['mobile']} style={{'--display': 'flex'}}>
            {!!includeCart && (
              <NavLink type="link" href="/cart" className={S['nav-button']}>
                <Icon icon="cart" size="100%" notify={notifyCart} />
              </NavLink>
            )}
            <AK.Disclosure
              render={
                <NavButton
                  right
                  icon={open ? 'close' : 'burger'}
                  {...tid('burger')}
                />
              }
            />
          </div>

          <div className={S['desktop']} style={{flex: '0 0 30rem'}}>
            <QuickSearch>
              {({search}) => (
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <div style={{flex: '0 1 100%'}}>
                    <QuickSearch.Input
                      search={search}
                      placeholder={quickSearchPlaceholder || undefined}
                      size="md"
                    />
                  </div>
                  <div>
                    <QuickSearch.Button
                      render={
                        <NavButton
                          icon="search"
                          style={{
                            height: '48px',
                            width: '54px',
                            padding: '10px',
                          }}
                        />
                      }
                    />
                  </div>
                  {!!includeCart && (
                    <NavLink
                      type="link"
                      href="/cart"
                      className={S['nav-button']}
                      style={{
                        height: '48px',
                        width: '54px',
                        padding: '10px',
                      }}
                    >
                      <Icon icon="cart" size="100%" notify={notifyCart} />
                    </NavLink>
                  )}
                </div>
              )}
            </QuickSearch>
          </div>
        </Container>

        <AK.DisclosureContent
          render={<div className={S['menu-holder']} {...tid('mobile-menu')} />}
        >
          <div className={S['menu']}>
            <Container>
              <Stack divided spacing="lg">
                <div />
                {navItems.map((props, i) => (
                  <MenuItem key={`nav.${i}`} {...props} />
                ))}
                {!!includeCart && (
                  <MenuItem type="link" label="Cart" href="/cart" />
                )}
                {profileItems
                  .filter(item => item.type !== 'separator')
                  .map((props, i) => (
                    <MenuItem key={`profile.${i}`} {...props} />
                  ))}
                <div />
              </Stack>
            </Container>
          </div>
        </AK.DisclosureContent>

        <div className={S['search-row']}>
          <Container className={S['search-row-items']}>
            <QuickSearch>
              {({search}) => (
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <div style={{flex: '0 1 100%', padding: '10px 0'}}>
                    <QuickSearch.Input
                      search={search}
                      size={'md' /* 44px tall */}
                      placeholder={quickSearchPlaceholder || undefined}
                      style={{
                        borderWidth: '1px', // XXX: clean up
                      }}
                    />
                  </div>
                  <QuickSearch.Button
                    render={<NavButton right icon="search" />}
                  />
                </div>
              )}
            </QuickSearch>
          </Container>
        </div>

        <Container className={S['nav-row']} {...tid('desktop-menu')}>
          <NavLink.Group>
            {navItems.map((props, i) => (
              <NavLink key={i} {...props} variant="nav" />
            ))}
          </NavLink.Group>
          <NavLink.Group>
            <ProfileMenu profileName={profileName} items={profileItems} />
          </NavLink.Group>
        </Container>

        <Divider className={S['nav-divider']} />
      </div>
    </AK.DisclosureProvider>
  )
}

export interface MinimalNavProps {
  logoLink: string
  logoUrl: string
  onClose: () => void
}

export const MinimalNav = ({logoLink, logoUrl, onClose}: MinimalNavProps) => {
  return (
    <div>
      <Container className={S['logo-row']}>
        <div className={S['logo']}>
          <Link href={logoLink} variant="unstyled">
            <Image loading="eager" src={logoUrl} />
          </Link>
        </div>
        <NavButton right icon="close" onClick={onClose} />
      </Container>
      <Divider />
    </div>
  )
}
